// src/app/services/payment-history.service.ts

import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';

// View-model types
import {
  PaymentHistory,
  PaymentHistoryItem
} from '../models/payment-history.model';

// DTO type
import { PaymentHistoryDto } from '../models/dtos/PaymentHistory.dto';

import { getHistoricPaymentsUrl } from '../shared/utils/api-urls.utils';

interface HistoricPaymentDto {
  Date:   string;
  type:   string;
  amount: number;
}

@Injectable({
  providedIn: 'root'
})
export class PaymentHistoryService {
  private readonly http = inject(HttpClient);

  /**
   * Gets payment history for a specific loan
   */
  getPaymentHistoryOld(loanId: string): Observable<PaymentHistory> {
    return this.http
      .get<PaymentHistoryDto[]>(
        getHistoricPaymentsUrl(loanId)
      )
      .pipe(
        map(rawArray => {
          if (!Array.isArray(rawArray)) {
            throw new Error('Payment history data is incomplete');
          }

          // 1️⃣ Flatten and coerce payment_number → non-nullable `number`
// inside map(rawArray => { … })
const flatItems: Array<PaymentHistoryItem & { _pn: number }> =
  rawArray.map((r, idx) => {
    const pn = idx;   // synthetic payment number

    return {
      datePaid:               r.Date   ? new Date(r.Date)    : null,
      dueDate:                r.due_date ? new Date(r.due_date) : null,
      paymentNumber:          pn,
      type:                   r.type,
      paymentAmount:          r.amount,
      transactionCode:        null,
      paymentMethod:          '',
      paymentMethodRef:       '',
      // ensure we always assign a number
      transactionReferenceNo: r.transaction_reference_no ?? pn,
      nsfPayment:             false,
      details:                [],
      _pn:                    pn,
    };
  });


          // 2️⃣ Group by our non-nullable _pn
          const grouped: Record<number, typeof flatItems[number][]> = {};
          for (const item of flatItems) {
            (grouped[item._pn] ||= []).push(item);
          }

          // 3️⃣ Build one parent row per paymentNumber, embedding all records as details
          const items: PaymentHistoryItem[][] = Object.values(grouped).map(group => {
            const total = group.reduce((sum, x) => sum + x.paymentAmount, 0);
            const parent: PaymentHistoryItem = {
              ...group[0],
              paymentAmount: total,
              details:       group.map(({ details, ...rest }) => ({
                // strip our _pn helper so it matches PaymentHistoryItem
                ...rest as Omit<typeof rest, '_pn'>,
              })),
            };
            return [parent];
          });

          return {
            loanId,
            loans: [],
            items
          };
        })
      );
  }

getPaymentHistory(loanId: string): Observable<PaymentHistory> {
  return this.http
    .get<PaymentHistoryDto[]>(getHistoricPaymentsUrl(loanId))
    .pipe(
      map(rawArray => {
        if (!Array.isArray(rawArray)) {
          throw new Error('Payment history data is incomplete');
        }

        // Use the array index as a stand-in "payment number"
        const flatItems: Array<PaymentHistoryItem & { _pn: number }> =
          rawArray.map((r, idx) => {
            const pn = idx;   // synthetic payment number
            return {
              datePaid:               r.Date   ? new Date(r.Date)    : null,
              dueDate:                null,  // if you don't have due dates
              paymentNumber:          pn,
              type:                   r.type,
              paymentAmount:          r.amount,
              transactionCode:        null,
              paymentMethod:          '',
              paymentMethodRef:       '',
              transactionReferenceNo: pn,
              nsfPayment:             false,
              details:                [],
              _pn:                    pn,
            };
          });

        // group by our synthetic _pn
        const grouped: Record<number, typeof flatItems[number][]> = {};
        for (const item of flatItems) {
          (grouped[item._pn] ||= []).push(item);
        }

        // build parent rows
        const items: PaymentHistoryItem[][] = Object.values(grouped).map(group => {
          const total = group.reduce((sum, x) => sum + x.paymentAmount, 0);
          const parent: PaymentHistoryItem = {
            ...group[0],
            paymentAmount: total,
            details:       group.map(({ _pn, ...rest }) => rest),
          };
          return [parent];
        });

        return {
          loanId,
          loans: [],
          items
        };
      })
    );
}




}
